import { api } from "./api";

/** Type de réduction : montant fixe (€) ou pourcentage (%) */
export type DiscountType = "FIXED" | "PERCENTAGE";

export interface EnterpriseDiscountRule {
  id: string;
  enterpriseId: string;
  label: string;
  indexMin: number;
  indexMax: number;
  discountType: DiscountType;
  discountValue: number;
  minChargeAmount: number;
  /** Prix fixe de livraison (€) pour les clients dans cette tranche d'indice. */
  fixedDeliveryPrice?: number | null;
  displayOrder: number;
}

export interface Enterprise {
  id: string;
  name: string;
  siret?: string;
  vatNumber?: string;
  vatRate?: number | null;
  address?: string;
  postalCode?: string;
  city?: string;
  phoneDialCode?: string;
  phone?: string;
  email?: string;
  contactPerson?: string;
  notes?: string;
  isActive: boolean;
  discountRules?: EnterpriseDiscountRule[];
}

export interface CreateEnterpriseData {
  name: string;
  siret?: string;
  vatNumber?: string;
  vatRate?: number | null;
  address?: string;
  postalCode?: string;
  city?: string;
  phoneDialCode?: string;
  phone?: string;
  email?: string;
  contactPerson?: string;
  notes?: string;
  isActive?: boolean;
}

export interface UpdateEnterpriseData extends Partial<CreateEnterpriseData> {}

/**
 * Service pour la gestion des entreprises
 */
export const enterpriseService = {
  /**
   * Récupère toutes les entreprises
   */
  async getAll(includeInactive = false, search?: string): Promise<Enterprise[]> {
    const params: Record<string, string> = {
      includeInactive: includeInactive ? "true" : "false",
    };
    if (search && search.trim()) params.search = search.trim();
    
    const response = await api.get<{ success: boolean; data: Enterprise[] }>(
      "/enterprises",
      { params }
    );
    return response.data.data;
  },
  
  /**
   * Récupère une entreprise par son ID (optionnellement avec les règles de réduction)
   */
  async getById(id: string, withDiscountRules = false): Promise<Enterprise> {
    const response = await api.get<{ success: boolean; data: Enterprise }>(
      `/enterprises/${id}`,
      { params: withDiscountRules ? { withDiscountRules: "true" } : {} }
    );
    return response.data.data;
  },

  /**
   * Met à jour les règles de réduction d'une entreprise (remplace toutes les règles)
   */
  async setDiscountRules(
    enterpriseId: string,
    rules: Omit<EnterpriseDiscountRule, "id" | "enterpriseId">[]
  ): Promise<Enterprise> {
    const response = await api.put<{ success: boolean; data: Enterprise }>(
      `/enterprises/${enterpriseId}/discount-rules`,
      { rules }
    );
    return response.data.data;
  },

  /**
   * Crée une nouvelle entreprise
   */
  async create(data: CreateEnterpriseData): Promise<Enterprise> {
    const response = await api.post<{ success: boolean; data: Enterprise }>(
      "/enterprises",
      data
    );
    return response.data.data;
  },

  /**
   * Met à jour une entreprise
   */
  async update(id: string, data: UpdateEnterpriseData): Promise<Enterprise> {
    const response = await api.put<{ success: boolean; data: Enterprise }>(
      `/enterprises/${id}`,
      data
    );
    return response.data.data;
  },

  /**
   * Supprime une entreprise
   */
  async delete(id: string): Promise<void> {
    await api.delete(`/enterprises/${id}`);
  },
};
